import React, { useState, useEffect } from 'react';

import {
  HeartOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Space,
  Divider,
  Timeline,
  Alert,
  Tabs,
  Button,
} from 'antd';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const { TabPane } = Tabs;

/**
 * ❤️ 2️⃣ TENANT HEALTH & STABILITY
 *
 * Shows:
 * - ⚙️ CPU Load
 * - 💾 Memory Usage
 * - 📊 Database Size (Mongo + Qdrant)
 * - 🌡️ API Latency
 * - 🔁 Retry Errors
 * - ❌ Error Rate
 * - 👤 Active Users Now
 * - 🚦 Uptime (30d, 7d, 24h)
 */
const TenantHealthDrawer = ({ visible, onClose, tenant }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && tenant) {
      fetchHealthData();
    }
  }, [visible, tenant]);

  const fetchHealthData = async () => {
    setLoading(true);
    try {
      const data = await SuperAdminAPI.getTenantHealth(tenant._id);
      const defaults = {
        overall: { score: 0, status: 'unknown', lastCheck: new Date().toISOString() },
        system: { cpu: 0, memory: 0, disk: 0 },
        database: { mongoSize: 0, qdrantSize: 0, totalDocuments: 0, totalVectors: 0 },
        api: { latency: 0, requestsPerMinute: 0, errorRate: 0, retryCount: 0 },
        users: { activeNow: 0, activeLast24h: 0, activeLast7d: 0 },
        uptime: { last24h: 0, last7d: 0, last30d: 0 },
        incidents: [],
      };
      setHealthData({
        ...defaults,
        ...data,
        overall: { ...defaults.overall, ...(data?.overall || {}) },
        system: { ...defaults.system, ...(data?.system || {}) },
        database: { ...defaults.database, ...(data?.database || {}) },
        api: { ...defaults.api, ...(data?.api || {}) },
        users: { ...defaults.users, ...(data?.users || {}) },
        uptime: { ...defaults.uptime, ...(data?.uptime || {}) },
        incidents: data?.incidents || [],
      });
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = score => {
    if (score >= 90) return '#52c41a';
    if (score >= 70) return '#faad14';
    return '#f5222d';
  };

  const getHealthStatus = score => {
    if (score >= 90) return { text: 'Healthy', icon: <CheckCircleOutlined />, color: 'success' };
    if (score >= 70) return { text: 'Warning', icon: <WarningOutlined />, color: 'warning' };
    return { text: 'Critical', icon: <CloseCircleOutlined />, color: 'error' };
  };

  if (!tenant) return null;

  const status = healthData ? getHealthStatus(healthData.overall.score) : null;

  return (
    <Drawer
      title={
        <Space>
          <HeartOutlined style={{ color: '#ff4d4f' }} />
          <span>Health Monitor - {tenant.name}</span>
        </Space>
      }
      placement="right"
      width={700}
      open={visible}
      onClose={onClose}
      extra={
        <Button icon={<SyncOutlined />} onClick={fetchHealthData} loading={loading}>
          Refresh
        </Button>
      }
    >
      {healthData && (
        <>
          {/* Overall Health Score */}
          <Card>
            <Row gutter={[16, 16]} align="middle">
              <Col span={12}>
                <Statistic
                  title="Overall Health Score"
                  value={healthData.overall.score}
                  suffix="/ 100"
                  valueStyle={{ color: getHealthColor(healthData.overall.score) }}
                />
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Tag
                  color={status.color}
                  icon={status.icon}
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  {status.text}
                </Tag>
              </Col>
            </Row>
            <Progress
              percent={healthData.overall.score}
              strokeColor={getHealthColor(healthData.overall.score)}
              showInfo={false}
              style={{ marginTop: '16px' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              Last checked: {new Date(healthData.overall.lastCheck).toLocaleString()}
            </div>
          </Card>

          <Divider />

          {/* Tabs for detailed metrics */}
          <Tabs defaultActiveKey="system">
            {/* ⚙️ System Resources */}
            <TabPane tab="⚙️ System" key="system">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Statistic
                    title="💻 CPU Load"
                    value={healthData.system.cpu}
                    suffix="%"
                    valueStyle={{ color: healthData.system.cpu > 80 ? '#f5222d' : '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.system.cpu}
                    strokeColor={healthData.system.cpu > 80 ? '#f5222d' : '#52c41a'}
                    style={{ marginTop: '8px' }}
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="💾 Memory Usage"
                    value={healthData.system.memory}
                    suffix="%"
                    valueStyle={{ color: healthData.system.memory > 80 ? '#f5222d' : '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.system.memory}
                    strokeColor={healthData.system.memory > 80 ? '#f5222d' : '#52c41a'}
                    style={{ marginTop: '8px' }}
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="💿 Disk Usage"
                    value={healthData.system.disk}
                    suffix="%"
                    valueStyle={{ color: healthData.system.disk > 80 ? '#f5222d' : '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.system.disk}
                    strokeColor={healthData.system.disk > 80 ? '#f5222d' : '#52c41a'}
                    style={{ marginTop: '8px' }}
                  />
                </Card>
              </Space>
            </TabPane>

            {/* 📊 Database */}
            <TabPane tab="📊 Database" key="database">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="🍃 MongoDB Size"
                        value={healthData.database.mongoSize}
                        suffix="GB"
                        prefix={<DatabaseOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="🔷 Qdrant Size"
                        value={healthData.database.qdrantSize}
                        suffix="GB"
                        prefix={<DatabaseOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="📄 Total Documents"
                        value={healthData.database.totalDocuments}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="🧠 Total Vectors"
                        value={healthData.database.totalVectors}
                      />
                    </Card>
                  </Col>
                </Row>

                <Alert
                  message="Database Health"
                  description="All database operations are running smoothly. No fragmentation detected."
                  type="success"
                  showIcon
                />
              </Space>
            </TabPane>

            {/* 🌡️ API Performance */}
            <TabPane tab="🌡️ API" key="api">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Statistic
                    title="⚡ Average Latency"
                    value={healthData.api.latency}
                    suffix="ms"
                    prefix={<ThunderboltOutlined />}
                    valueStyle={{ color: healthData.api.latency > 200 ? '#faad14' : '#52c41a' }}
                  />
                  {healthData.api.latency > 200 && (
                    <Alert
                      message="High latency detected"
                      type="warning"
                      showIcon
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </Card>

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="📊 Requests/min"
                        value={healthData.api.requestsPerMinute}
                        prefix={<ApiOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small">
                      <Statistic
                        title="❌ Error Rate"
                        value={healthData.api.errorRate}
                        suffix="%"
                        valueStyle={{ color: healthData.api.errorRate > 2 ? '#f5222d' : '#52c41a' }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Card size="small">
                  <Statistic title="🔁 Retry Count (24h)" value={healthData.api.retryCount} />
                </Card>
              </Space>
            </TabPane>

            {/* 👤 Active Users */}
            <TabPane tab="👤 Users" key="users">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Statistic
                    title="🟢 Active Now"
                    value={healthData.users.activeNow}
                    suffix="users"
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="📅 Active Last 24h"
                    value={healthData.users.activeLast24h}
                    suffix="users"
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="📆 Active Last 7d"
                    value={healthData.users.activeLast7d}
                    suffix="users"
                  />
                </Card>
              </Space>
            </TabPane>

            {/* 🚦 Uptime */}
            <TabPane tab="🚦 Uptime" key="uptime">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Statistic
                    title="⏱️ Last 24 Hours"
                    value={healthData.uptime.last24h}
                    suffix="%"
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.uptime.last24h}
                    strokeColor="#52c41a"
                    style={{ marginTop: '8px' }}
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="📅 Last 7 Days"
                    value={healthData.uptime.last7d}
                    suffix="%"
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.uptime.last7d}
                    strokeColor="#52c41a"
                    style={{ marginTop: '8px' }}
                  />
                </Card>

                <Card size="small">
                  <Statistic
                    title="📆 Last 30 Days"
                    value={healthData.uptime.last30d}
                    suffix="%"
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Progress
                    percent={healthData.uptime.last30d}
                    strokeColor="#52c41a"
                    style={{ marginTop: '8px' }}
                  />
                </Card>
              </Space>
            </TabPane>

            {/* 📜 Incidents */}
            <TabPane tab="📜 Incidents" key="incidents">
              <Timeline>
                {healthData.incidents.map((incident, index) => {
                  const iconMap = {
                    error: <CloseCircleOutlined style={{ color: '#f5222d' }} />,
                    warning: <WarningOutlined style={{ color: '#faad14' }} />,
                    info: <CheckCircleOutlined style={{ color: '#1890ff' }} />,
                  };

                  return (
                    <Timeline.Item key={index} dot={iconMap[incident.type]}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{incident.time}</div>
                        <div>{incident.message}</div>
                        {incident.resolved && (
                          <Tag color="success" style={{ marginTop: '4px' }}>
                            Resolved
                          </Tag>
                        )}
                      </div>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </TabPane>
          </Tabs>
        </>
      )}
    </Drawer>
  );
};

export default TenantHealthDrawer;
